import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { validateTenant } from '../lib/tenant';
import { useAuthStore } from '../stores/authStore';
import { Loader2 } from 'lucide-react';

/**
 * TenantRoute Component
 * Validates that the tenant exists and is active before rendering children
 * Shows loading state while validating
 * Shows error if tenant is invalid or inactive
 */
const TenantRoute = ({ children }) => {
  const { tenantId } = useParams();
  const { logout } = useAuthStore();
  const [validationState, setValidationState] = useState({
    isLoading: true,
    isValid: false,
    error: null
  });

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  useEffect(() => {
    const checkTenant = async () => {
      const result = await validateTenant(tenantId);
      
      setValidationState({
        isLoading: false,
        isValid: result.valid,
        error: result.error
      });
    };

    checkTenant();
  }, [tenantId]);

  if (validationState.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-fuchsia-50 to-purple-100">
        <div className="flex flex-col items-center">
          <Loader2 className="animate-spin text-purple-600" size={48} />
          <p className="mt-4 text-gray-500">Loading organization...</p>
        </div>
      </div>
    );
  }

  if (!validationState.isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-fuchsia-50 to-purple-100 p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Organization Not Found</h1>
          <p className="text-gray-600 mb-2">
            We couldn&apos;t find an organization with the code <strong>{tenantId}</strong>.
          </p>
          <p className="text-gray-500 text-sm mb-6">
            {validationState.error || 'Please check the URL and try again.'}
          </p>
          <div className="space-y-3">
            <button 
              onClick={handleLogout}
              className="inline-block w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              Go to Login
            </button>
            <a 
              href="/signup" 
              className="inline-block w-full px-6 py-3 bg-white text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-medium"
            >
              Create New Organization
            </a>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default TenantRoute;
